import { useEffect, useRef, useState } from 'react'
import { FiArrowLeft, FiPlus, FiSettings, FiX } from 'react-icons/fi'
import { useLocation, useSearchParams } from 'react-router-dom'
import { logoutWithPushTokenCleanup } from '@domain/features/auth/auth-feature'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import { UserAnalyticsFeature } from '@domain/features/user-analytics/user-analytics-feature'
import {
  deleteUser as deleteUserAccount,
  saveName as saveUserName,
  uploadProfilePhoto,
} from '@domain/features/user/user-feature'
import PrimaryButton from '@presentation/components/ui/PrimaryButton'
import { useAuthUser } from '@presentation/hooks/useAuthUser'

type ProfileView = 'profile' | 'settings'

export default function UserProfilePage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { user, isAuthenticated } = useAuthUser()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingName, setIsSavingName] = useState(false)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [fullName, setFullName] = useState('')
  const [activeView, setActiveView] = useState<ProfileView>('profile')
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('')
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [nameSaveMessage, setNameSaveMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (user) {
      setFullName(user.fullName ?? '')
    }
  }, [user])

  const closeModal = (force = false) => {
    if (isDeletingAccount && !force) {
      return
    }
    const next = new URLSearchParams(searchParams)
    next.delete('userProfile')
    setSearchParams(next, { replace: true })
    setActiveView('profile')
    setIsDeleteConfirmationOpen(false)
    setDeleteConfirmationInput('')
    setDeletePassword('')
    setDeleteError(null)
    setNameSaveMessage(null)
    setError(null)
  }

  const openSettingsView = () => {
    if (isDeletingAccount) {
      return
    }
    setActiveView('settings')
    setError(null)
    setDeleteError(null)
  }

  const closeDeleteConfirmation = () => {
    if (isDeletingAccount) {
      return
    }
    setIsDeleteConfirmationOpen(false)
    setDeleteConfirmationInput('')
    setDeletePassword('')
    setDeleteError(null)
  }

  const openDeleteConfirmation = () => {
    if (isDeletingAccount) {
      return
    }
    setDeleteError(null)
    setDeleteConfirmationInput('')
    setDeletePassword('')
    setIsDeleteConfirmationOpen(true)
  }

  const mapDeleteError = (deleteAccountError: unknown): string => {
    if (deleteAccountError instanceof CoreError) {
      if (deleteAccountError.cause === CoreErrorCause.InvalidCredential) {
        return 'Current password is incorrect. Please try again.'
      }
      if (deleteAccountError.cause === CoreErrorCause.RequireRecentAuthentication) {
        return 'For your safety, please sign in again and retry account deletion.'
      }
      if (
        deleteAccountError.cause === CoreErrorCause.NetworkConnection ||
        deleteAccountError.cause === CoreErrorCause.NetworkError
      ) {
        return 'Network issue detected. Please check your connection and try again.'
      }
      return deleteAccountError.context ?? 'Failed to delete your account.'
    }

    if (deleteAccountError instanceof Error) {
      return deleteAccountError.message
    }

    return 'Failed to delete your account.'
  }

  const persistFullNameIfChanged = async () => {
    if (!user || isSavingName || isUploadingPhoto) {
      return
    }

    const trimmedCurrentName = (user.fullName ?? '').trim()
    const trimmedNextName = fullName.trim()

    if (trimmedCurrentName === trimmedNextName) {
      return
    }
    if (!trimmedNextName) {
      setError('Full name cannot be empty.')
      setFullName(user.fullName ?? '')
      return
    }

    setIsSavingName(true)
    setNameSaveMessage(null)
    setError(null)
    try {
      await saveUserName(trimmedNextName)
      UserAnalyticsFeature.trackProfileNameSave(screenPath, true)
      closeModal()
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save full name.')
      setFullName(user.fullName ?? '')
    } finally {
      setIsSavingName(false)
    }
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file || isUploadingPhoto || isSavingName) {
      return
    }

    setIsUploadingPhoto(true)
    setError(null)
    try {
      await uploadProfilePhoto(file)
      UserAnalyticsFeature.trackProfilePhotoUpload(screenPath, true)
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload photo.')
    } finally {
      setIsUploadingPhoto(false)
    }
  }

  const handleAvatarClick = () => {
    if (isUploadingPhoto || isSavingName || isSubmitting) {
      return
    }
    fileInputRef.current?.click()
  }

  const handleLogout = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await logoutWithPushTokenCleanup()
      UserAnalyticsFeature.trackAuthLogout(screenPath, true)
      closeModal()
    } catch (logoutError) {
      setError(logoutError instanceof Error ? logoutError.message : 'Failed to log out.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true)
    setDeleteError(null)
    try {
      await deleteUserAccount(deletePassword)
      closeModal(true)
    } catch (deleteAccountError) {
      setDeleteError(mapDeleteError(deleteAccountError))
    } finally {
      setIsDeletingAccount(false)
    }
  }

  if (!isAuthenticated || !user) {
    return null
  }

  const trimmedCurrentName = (user.fullName ?? '').trim()
  const trimmedNextName = fullName.trim()
  const hasNameChanges = trimmedCurrentName !== trimmedNextName
  const isSaveDisabled =
    isSavingName || isUploadingPhoto || isSubmitting || !hasNameChanges || !trimmedNextName
  const deleteKeyword = deleteConfirmationInput.trim()
  const isDeleteKeywordValid = deleteKeyword === 'DELETE' || deleteKeyword === 'delete'
  const canDeleteAccount =
    isDeleteKeywordValid && deletePassword.trim().length > 0 && !isDeletingAccount

  return (
    <div className="fixed inset-0 z-60 bg-black/55">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close user profile modal"
        onClick={() => {
          closeModal()
        }}
        disabled={isDeletingAccount}
      />

      <div className="relative z-10 flex h-full w-full items-end justify-center md:items-center md:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label={activeView === 'profile' ? 'User profile' : 'User settings'}
          className="relative w-full rounded-t-2xl bg-blue px-6 py-7 text-white shadow-2xl md:max-w-sm md:rounded-2xl"
        >
          {activeView === 'profile' ? (
            <button
              type="button"
              onClick={openSettingsView}
              aria-label="Open settings"
              className="absolute left-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/15"
            >
              <FiSettings className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (isDeletingAccount) {
                  return
                }
                setActiveView('profile')
                closeDeleteConfirmation()
              }}
              aria-label="Back to profile"
              disabled={isDeletingAccount}
              className="absolute left-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FiArrowLeft className="h-5 w-5" />
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              closeModal()
            }}
            aria-label="Close user profile modal"
            disabled={isDeletingAccount}
            className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <FiX className="h-5 w-5" />
          </button>

          {activeView === 'profile' ? (
            <>
              <div className="mt-2 flex flex-col items-stretch">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />

                <button
                  type="button"
                  onClick={handleAvatarClick}
                  disabled={isUploadingPhoto || isSavingName || isSubmitting || isDeletingAccount}
                  className="group relative inline-flex h-24 w-24 self-center items-center justify-center overflow-hidden rounded-full border-2 border-white/70 bg-white/10 transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-70"
                  aria-label="Upload profile picture"
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt="User avatar"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="inline-flex h-full w-full items-center justify-center">
                      <FiPlus className="h-8 w-8 text-white" />
                    </span>
                  )}

                  {isUploadingPhoto ? (
                    <span className="absolute inset-0 inline-flex items-center justify-center bg-black/50 text-xs font-semibold">
                      Uploading...
                    </span>
                  ) : null}
                </button>

                <p className="mt-3 text-center text-xs font-medium text-white/80">
                  Tap image to upload or take a photo
                </p>

                <div className="mt-4 w-full text-left">
                  <p className="text-sm font-semibold text-white/85">Email</p>
                  <p className="mt-1 w-full break-all text-base font-bold">{user.email}</p>
                </div>

                <div className="mt-5 w-full text-left">
                  <label
                    htmlFor="profile-full-name"
                    className="block text-sm font-medium text-white/75"
                  >
                    Full name
                  </label>
                  <input
                    id="profile-full-name"
                    type="text"
                    value={fullName}
                    onChange={(event) => {
                      setFullName(event.target.value)
                      setNameSaveMessage(null)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        event.currentTarget.blur()
                      }
                    }}
                    disabled={isSavingName || isUploadingPhoto || isSubmitting || isDeletingAccount}
                    className="mt-2 w-full rounded-xl border border-white/15 bg-white/8 px-3 py-2.5 text-base font-normal text-white outline-none transition-colors placeholder:text-white/60 focus:border-white/20 focus:outline-none focus:ring-0 focus-visible:border-white/20 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-70"
                    placeholder="Enter your full name"
                  />
                  <p className="mt-2 text-left text-xs text-white/75">
                    Update your name, then tap Save to keep the change.
                  </p>
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      void persistFullNameIfChanged()
                    }}
                    disabled={isSaveDisabled || isDeletingAccount}
                    className="mt-4 w-full"
                  >
                    {isSavingName ? 'Saving...' : 'Save'}
                  </PrimaryButton>
                  {nameSaveMessage ? (
                    <p className="mt-2 text-left text-xs font-semibold text-white/90">
                      {nameSaveMessage}
                    </p>
                  ) : null}
                </div>
              </div>

              {error ? (
                <p className="mt-5 rounded-lg border border-white/35 bg-white/10 px-3 py-2 text-sm">
                  {error}
                </p>
              ) : null}

              <div className="mt-10 border-t border-white/25 pt-7">
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isSubmitting || isDeletingAccount}
                  className="w-full text-center text-sm font-semibold text-white underline underline-offset-2 transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? 'Logging out...' : 'Log out'}
                </button>
              </div>
            </>
          ) : (
            <div className="mt-10 flex min-h-[360px] flex-col">
              <h2 className="text-center text-xl font-semibold text-white">Settings</h2>
              <p className="mt-3 text-center text-sm text-white/80">
                Manage sensitive account actions separately from your profile details.
              </p>

              <div className="mt-6 rounded-xl border border-white/20 bg-white/8 px-4 py-4">
                <p className="text-sm font-semibold text-white">Account safety</p>
                <p className="mt-1 text-xs text-white/75">
                  Account deletion requires confirmation typing and your current password.
                </p>
              </div>

              <div className="mt-auto border-t border-white/20 pt-10">
                <button
                  type="button"
                  onClick={openDeleteConfirmation}
                  disabled={isDeletingAccount}
                  className="w-full text-center text-xs font-medium text-white/55 transition-colors hover:text-white/75 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </section>

        {isDeleteConfirmationOpen ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/55 px-4">
            <section
              role="dialog"
              aria-modal="true"
              aria-label="Delete account confirmation"
              className="w-full max-w-sm rounded-2xl bg-blue px-5 py-6 text-white shadow-2xl"
            >
              <h2 className="text-xl font-bold text-white">Delete Account?</h2>
              <p className="mt-3 rounded-lg border border-red-300 bg-red-50/95 px-3 py-2 text-sm font-semibold text-red-700">
                Warning: This action is permanent and cannot be undone. You will lose access to all
                your saved data, webinars, and settings.
              </p>
              <p className="mt-4 text-sm text-white/90">
                To confirm that you want to permanently delete your account, please type the word{' '}
                <strong>DELETE</strong> in the box below.
              </p>

              <form
                className="mt-5 space-y-4"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (!canDeleteAccount) {
                    return
                  }
                  void handleDeleteAccount()
                }}
              >
                <div className="space-y-1.5">
                  <label
                    htmlFor="delete-confirmation-word"
                    className="text-sm font-semibold text-white/90"
                  >
                    Confirmation word
                  </label>
                  <input
                    id="delete-confirmation-word"
                    type="text"
                    value={deleteConfirmationInput}
                    onChange={(event) => setDeleteConfirmationInput(event.target.value)}
                    disabled={isDeletingAccount}
                    placeholder="Type DELETE here"
                    className="w-full rounded-lg border border-white/35 bg-white/12 px-3 py-2.5 text-base text-white outline-none transition-colors placeholder:text-white/65 focus:border-white/60 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="delete-current-password" className="text-sm font-semibold text-white/90">
                    Current password
                  </label>
                  <input
                    id="delete-current-password"
                    type="password"
                    value={deletePassword}
                    onChange={(event) => setDeletePassword(event.target.value)}
                    disabled={isDeletingAccount}
                    placeholder="Enter current password"
                    className="w-full rounded-lg border border-white/35 bg-white/12 px-3 py-2.5 text-base text-white outline-none transition-colors placeholder:text-white/65 focus:border-white/60 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-70"
                  />
                </div>

                {deleteError ? (
                  <p className="rounded-lg border border-red-100/40 bg-red-500/20 px-3 py-2 text-sm text-red-50">
                    {deleteError}
                  </p>
                ) : null}

                <button
                  type="button"
                  onClick={closeDeleteConfirmation}
                  disabled={isDeletingAccount}
                  className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-white px-4 text-base font-semibold text-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  Cancel, go back
                </button>

                <button
                  type="submit"
                  disabled={!canDeleteAccount}
                  className={`inline-flex h-12 w-full items-center justify-center rounded-lg px-4 text-base font-semibold text-white transition-opacity ${
                    canDeleteAccount ? 'bg-red-600 hover:opacity-90' : 'bg-white/35'
                  } disabled:cursor-not-allowed disabled:opacity-70`}
                >
                  {isDeletingAccount ? 'Deleting account...' : 'Permanently Delete'}
                </button>
              </form>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  )
}
